/**
 * Zod schema for JSON import/export helper (future export format).
 */
import { LayoutOrientation, NodeType } from '@cadet-dsl';
import { IStringSchema, LocaleId } from '@strings';
import { z } from 'zod';

/* ------------------------------------------------- */
/*  Primitive helpers                                */
/* ------------------------------------------------- */
export const FuidSchema = z.string().min(1);

/* ------------------------------------------------- */
/*  Node & Edge sub-schemas                          */
/* ------------------------------------------------- */
export const NodeDataSchema = z.object({
  type: z.nativeEnum(NodeType),
  title: IStringSchema,
  // bounds represented as [l,t,r,b]
  bounds: z.tuple([z.number(), z.number(), z.number(), z.number()]).optional(),
  color: z.string().optional() // group colour id
});
export type NodeDataSchemaType = z.infer<typeof NodeDataSchema>;

export const EdgeDataSchema = z.object({
  path: z.string().optional() // SVG path data
});
export type EdgeDataSchemaType = z.infer<typeof EdgeDataSchema>;

/* ------------------------------------------------- */
/*  Graph container                                  */
/* ------------------------------------------------- */
export const GraphSchema = z.object({
  nodes: z.array(z.string()),
  edges: z.array(z.string()),
  // maps: id -> data
  nodeData: z.record(NodeDataSchema),
  edgeData: z.record(EdgeDataSchema),
  parents: z.record(z.string()).optional(), // childId -> parentId
  sources: z.record(z.string()).optional(), // edgeId  -> sourceId
  targets: z.record(z.string()).optional() // edgeId  -> targetId
});

/* ------------------------------------------------- */
/*  Document schema                                  */
/* ------------------------------------------------- */
export const DocSchema = z.object({
  type: z.literal('CADET Document'),
  version: z.number().int(),
  id: FuidSchema,
  settings: z
    .object({
      orientation: z.nativeEnum(LayoutOrientation).optional(),
      alignRoots: z.boolean().optional(),
      alignLeaves: z.boolean().optional(),
      bundleEdges: z.boolean().optional(),
      locale: z.nativeEnum(LocaleId).optional()
    })
    .optional(),
  graph: GraphSchema
});
